import React, { useState, useMemo, useRef, useEffect } from 'react';

// ux
import LoadingAnimation from '../ux/LoadingAnimation.js';
import LaunchplanItem from '../ux/LaunchplanItem';

const Launchplans = ({
  projects,
  dashboardLoading,
  handleOpenModal,
  fetchProject,
  handleViewChange,
  renameProject
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('date');
  const [selectedProjects, setSelectedProjects] = useState([]);

  const selectAllRef = useRef();

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  const handleProjectSelect = (projectId) => {
    setSelectedProjects((prevSelected) => {
      if (prevSelected.includes(projectId)) {
        // If already selected, remove it
        return prevSelected.filter((id) => id !== projectId);
      } else {
        // If not selected, add it
        return [...prevSelected, projectId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedProjects.length === sortedFilteredProjects.length) {
      // If all are selected, deselect all
      setSelectedProjects([]);
    } else {
      // If not all are selected, select all
      const allProjectIds = sortedFilteredProjects.map((project) => project.ID);
      setSelectedProjects(allProjectIds);
    }
  };

  const sortedFilteredProjects = useMemo(() => {
    // Step 4.1: Filter projects based on the search term
    let filtered = projects;
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = projects.filter(
        (project) =>
          (project.project_name &&
            project.project_name.toLowerCase().includes(lowerSearchTerm)) ||
          (project.brand_name &&
            project.brand_name.toLowerCase().includes(lowerSearchTerm))
      );
    }

    // Step 4.2: Sort the filtered projects based on the selected sort option
    let sorted = [...filtered]; // Create a shallow copy to avoid mutating the original array

    if (sortOption === 'date') {
      // Assuming 'date' is a valid date string or Date object in your project
      sorted.sort((a, b) => new Date(a.date) - new Date(b.date)); // Ascending order (oldest first)
      // For descending order (newest first), use: new Date(b.date) - new Date(a.date)
    } else if (sortOption === 'name') {
      sorted.sort((a, b) => {
        // Determine the names to compare
        const aName = a.project_name || a.brand_name || '';
        const bName = b.project_name || b.brand_name || '';
        return aName.localeCompare(bName);
      });
    }

    return sorted;
  }, [searchTerm, sortOption, projects]);

  useEffect(() => {
    if (
      selectedProjects.length > 0 &&
      selectedProjects.length < sortedFilteredProjects.length
    ) {
      selectAllRef.current.indeterminate = true;
    } else {
      selectAllRef.current.indeterminate = false;
    }
  }, [selectedProjects, sortedFilteredProjects.length]);

  return (
    <div className="fuselight-dashboard-container">
      <div className="dashboard-bg">
        <div></div>
        <div></div>
        <div></div>
      </div>
      <div className="search-and-sort-container">
        <div className="sort-container">
          <label htmlFor="sort-select" className="sort-label">
            Sort By:
          </label>
          <select
            id="sort-select"
            value={sortOption}
            onChange={handleSortChange}
            className="sort-select"
            aria-label="Sort Launchplans"
          >
            <option value="date">Recent</option>
            <option value="name">Title</option>
          </select>
        </div>
        <div className="search-container">
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
            aria-label="Search Launchplans"
          />
        </div>
        <button
          className="delete-button"
          onClick={() => handleOpenModal('deleteProjects', selectedProjects)}
          disabled={selectedProjects.length === 0}
        >
          <i class="fas fa-trash-alt"></i>
        </button>
      </div>

      <h3>All Launchplans</h3>
      <div className="fuselight-dashboard-header">
        <div className="fuselight-dashboard-header-checkbox">
          <label className="custom-checkbox">
            <input
              type="checkbox"
              ref={selectAllRef}
              checked={
                selectedProjects.length === sortedFilteredProjects.length &&
                sortedFilteredProjects.length > 0
              }
              onChange={handleSelectAll}
              aria-label="Select All Launchplans"
            />
            <span></span>
          </label>
        </div>
        <h4>Title</h4>
        <h4>Fusions</h4>
        <h4>Date</h4>
      </div>
      <div className="fuselight-dashboard-content">
        {dashboardLoading ? (
          <LoadingAnimation label="Loading Projects" />
        ) : (
          <>
            {sortedFilteredProjects.length > 0 ? (
              <div className="fuselight-dashboard-list">
                {sortedFilteredProjects.map((project) => (
                  <LaunchplanItem
                    key={project.ID}
                    project={project}
                    handleOpenModal={handleOpenModal}
                    fetchProject={fetchProject}
                    isSelected={selectedProjects.includes(project.ID)}
                    onSelectChange={() => handleProjectSelect(project.ID)}
                    renameProject={renameProject}
                  />
                ))}
              </div>
            ) : (
              <div className="dashboard-none-found">
                <p>No launchplans found</p>
                <button
                  className="btn btn--secondary-outline"
                  onClick={() => handleViewChange('launchpad')}
                >
                  Create One
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Launchplans;
